/**
 * Propagate edits to drafts in other countries that share the same static tag(s).
 */

import { useMemo, useState } from 'react';
import { Tags } from 'lucide-react';

import { Button } from '@/components/ui';
import { ModalSection } from '@/features/smswiser-campaigns/components/ScaleDropModal/shared';
import { cn } from '@/lib/utils';

import { StaticTagCountryTable } from './StaticTagCountryTable';
import {
  propagatableGroups,
  staticTagFilterButtonStyle,
  tagNamesFromGroups,
  withoutBulkSourceKeys,
} from './staticTagPropagationUtils';
import type { StaticTagPropagationProps } from './staticTagPropagation.types';

export type { StaticTagPropagationProps } from './staticTagPropagation.types';

const DEFAULT_COUNT_UNIT = { singular: 'draft', plural: 'drafts' } as const;
const DEFAULT_DESCRIPTION =
  'First row: your bulk-edit country (always included). Other rows add matching drafts by shared tag.';

export function StaticTagPropagation(props: StaticTagPropagationProps) {
  const {
    groups,
    staticTagColors = {},
    totalMatchCount,
    matchCountUnit = DEFAULT_COUNT_UNIT,
    description = DEFAULT_DESCRIPTION,
    selectedCountries,
    onToggle,
    onSelectAll,
    onDeselectAll,
    onAddCountries,
    onRemoveCountries,
    disabled,
  } = props;
  const [tagFilter, setTagFilter] = useState<string | null>(null);

  const tagOptions = useMemo(() => tagNamesFromGroups(groups), [groups]);
  const filteredGroups = useMemo(() => {
    if (!tagFilter) return groups;
    return groups.filter((g) => g.tags.some((t) => t.tagName === tagFilter));
  }, [groups, tagFilter]);

  if (groups.length === 0) return null;

  const globalTargets = propagatableGroups(groups);
  const allGlobalSelected =
    globalTargets.length > 0 && globalTargets.every((g) => selectedCountries.includes(g.countryKey));
  const filteredKeys = filteredGroups.map((g) => g.countryKey);
  const filteredTargets = propagatableGroups(filteredGroups);
  const filteredAllSelected =
    filteredTargets.length > 0 && filteredTargets.every((g) => selectedCountries.includes(g.countryKey));
  const scopeAllSelected = tagFilter == null ? allGlobalSelected : filteredAllSelected;

  const handleHeaderBulk = () => {
    if (!tagFilter) {
      if (allGlobalSelected) onDeselectAll();
      else onSelectAll();
      return;
    }
    const keys = withoutBulkSourceKeys(groups, filteredKeys);
    if (filteredAllSelected) onRemoveCountries(keys);
    else onAddCountries(keys);
  };

  return (
    <ModalSection
      title="Match other countries by static tag"
      icon={<Tags className="h-3.5 w-3.5" />}
      badge={
        <span className="ml-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
          {totalMatchCount}{' '}
          {totalMatchCount === 1 ? matchCountUnit.singular : matchCountUnit.plural}
        </span>
      }
      headerAction={
        <button
          type="button"
          className="text-xs font-medium text-primary hover:underline"
          onClick={handleHeaderBulk}
          disabled={disabled || (tagFilter != null && filteredGroups.length === 0)}
          aria-label={scopeAllSelected ? 'Deselect all' : 'Select all'}
        >
          {scopeAllSelected ? 'Deselect all' : 'Select all'}
        </button>
      }
    >
      <p className="mb-3 text-xs leading-relaxed text-muted-foreground">{description}</p>

      {tagOptions.length > 1 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Tag</span>
          <Button
            type="button"
            size="sm"
            variant={tagFilter == null ? 'secondary' : 'ghost'}
            className="h-7 rounded-full px-2.5 text-xs"
            onClick={() => setTagFilter(null)}
            disabled={disabled}
          >
            All
          </Button>
          {tagOptions.map((name) => {
            const hex = staticTagColors[name];
            const selected = tagFilter === name;
            const coloredStyle = staticTagFilterButtonStyle(hex, selected);
            return (
              <Button
                key={name}
                type="button"
                size="sm"
                variant={coloredStyle ? 'ghost' : selected ? 'secondary' : 'ghost'}
                style={coloredStyle}
                className={cn(
                  'h-7 max-w-[200px] truncate rounded-full px-2.5 text-xs',
                  coloredStyle && 'border border-solid hover:opacity-90',
                )}
                title={name}
                onClick={() => setTagFilter(name)}
                disabled={disabled}
              >
                {name}
              </Button>
            );
          })}
        </div>
      )}

      <StaticTagCountryTable
        groups={filteredGroups}
        staticTagColors={staticTagColors}
        selectedCountries={selectedCountries}
        onToggle={onToggle}
        disabled={disabled}
      />

      {tagFilter != null && filteredGroups.length === 0 && (
        <p className="mt-2 text-xs text-muted-foreground">No countries for this tag.</p>
      )}
    </ModalSection>
  );
}
